'use client';

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { trackPageDuration, analytics } from '@/src/utils/analytics';
import { Flame, Clock, Download, Filter, ArrowDown, ArrowUp, Heart, Layers } from 'lucide-react';
import { useMediaQuery } from '@/lib/hooks/use-media-query';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/src/components/ui/dropdown-menu';
import { BoardSidebar } from './board-sidebar';
import { TYPE_BOARDS } from '@/lib/community/boards';
import { CommunityTabs } from './community-tabs';
import { COMMUNITY_FEED_COLUMNS } from './community-config';
import { PostFeed } from './post-feed';
import { PostCard } from './post-card';
import { CommunityRightSidebar } from './community-right-sidebar';
import { useAppContext } from '@/lib/app-context';
import { useFeed } from '@/lib/community/hooks/use-feed';
import { togglePostLike, togglePostFavorite } from '@/lib/community/service/interaction-service';
import type { CommunityPost, FeedTab, AbilityType, FeedParams } from '@/lib/community/types';

const SORT_OPTIONS: { key: 'hot' | 'new' | 'top' | 'likes'; label: string; icon: typeof Flame; descLabel: string; ascLabel: string }[] = [
	{ key: 'hot', label: '热榜', icon: Flame, descLabel: '从高到低', ascLabel: '从低到高' },
	{ key: 'new', label: '最新', icon: Clock, descLabel: '从新到旧', ascLabel: '从旧到新' },
	{ key: 'top', label: '最多复制', icon: Download, descLabel: '从多到少', ascLabel: '从少到多' },
	{ key: 'likes', label: '最多点赞', icon: Heart, descLabel: '从多到少', ascLabel: '从少到多' },
];

/**
 * CommunityLandingPage — 瀑布流社区 Feed 页面
 *
 * 布局: TopBar（全局）+ 二级 Tab 栏 + 瀑布流 Feed + 右侧栏
 * 点击帖子卡片跳转到独立详情页 /community/post/:id
 */
export function CommunityLandingPage() {
	const { setWaveIntensity } = useAppContext();
	const [searchParams, setSearchParams] = useSearchParams();
	const isNarrow = useMediaQuery('(max-width: 1023px)');
	const hideRightSidebar = useMediaQuery('(max-width: 1100px)');

	useEffect(() => trackPageDuration('community'), []);

	/* ── Listen for TopBar search event ── */
	useEffect(() => {
		const handler = (e: Event) => {
			const q = (e as CustomEvent<string>).detail;
			setSearchQuery(q ?? '');
		};
		window.addEventListener('safeclaw:search', handler);
		return () => window.removeEventListener('safeclaw:search', handler);
	}, []);

	/* ── State ── */
	const [activeTab, setActiveTab] = useState<FeedTab>('recommended');
	const [activeType, setActiveType] = useState<AbilityType | undefined>((searchParams.get('type') as AbilityType) || undefined);
	const [activeTag, setActiveTag] = useState<string | null>(searchParams.get('tag') || null);
	const [activeBoardSlug, setActiveBoardSlug] = useState<string | null>(searchParams.get('board') || null);
	const [searchQuery, setSearchQuery] = useState('');
	const [debouncedSearch, setDebouncedSearch] = useState('');
	const [sort, setSort] = useState<'hot' | 'new' | 'top' | 'likes'>('hot');
	const [sortDirection, setSortDirection] = useState<'desc' | 'asc'>('desc');

	/* ── Feed params ── */
	const feedParams = useMemo<FeedParams>(
		() => ({
			tab: activeTab,
			abilityType: activeType,
			boardSlug: activeBoardSlug ?? undefined,
			search: debouncedSearch || undefined,
			tag: activeTag ?? undefined,
			sort,
			sortDirection,
			pageSize: 12,
		}),
		[activeTab, activeType, activeBoardSlug, debouncedSearch, activeTag, sort, sortDirection],
	);

	/* ── Feed data via hook ── */
	const { posts, loading: isLoading, hasMore, loadMore, refresh: refreshFeed } = useFeed(feedParams);

	/* ── Consume URL params (?type=, ?board=, ?tag=) on mount ── */
	useEffect(() => {
		const hasParams = searchParams.get('type') || searchParams.get('board') || searchParams.get('tag');
		if (hasParams) {
			setSearchParams({}, { replace: true });
		}
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	/* ── Debounce search query (300ms) ── */
	useEffect(() => {
		const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
		return () => clearTimeout(timer);
	}, [searchQuery]);

	/* ── Dim wave background on mount ── */
	useEffect(() => {
		setWaveIntensity(0.2);
		return () => setWaveIntensity(1);
	}, [setWaveIntensity]);

	/* ── Refresh feed when a new post is published ── */
	useEffect(() => {
		const handler = () => refreshFeed();
		window.addEventListener('safeclaw:post-published', handler);
		return () => window.removeEventListener('safeclaw:post-published', handler);
	}, [refreshFeed]);

	const handleTagClick = useCallback((tag: string | null) => {
		setActiveTag(tag);
	}, []);

	const handleBoardSelect = useCallback((slug: string | null) => {
		setActiveBoardSlug(slug);
		setActiveType(undefined);
		analytics.track('CommunityClickCategory', { type: 'board', value: slug ?? 'all' });
	}, []);

	const handleBoardTypeSelect = useCallback((type: AbilityType | undefined) => {
		setActiveType(type);
		analytics.track('CommunityClickCategory', { type: 'ability_type', value: type ?? 'all' });
	}, []);

	const handleLike = useCallback(async (post: CommunityPost) => {
		await togglePostLike(post.id);
	}, []);

	const handleFavorite = useCallback(async (post: CommunityPost) => {
		await togglePostFavorite(post.id);
	}, []);

	return (
		<div className="h-full w-full overflow-y-auto pt-14">
			{/* Main layout — three-column centered */}
			<div className="max-w-[1400px] mx-auto flex items-start gap-5 px-4 sm:px-6 pt-2 pb-4">
				{/* ── Left sidebar — board list (hidden in detail view & narrow screens) ── */}
				{!isNarrow && (
					<aside className="w-[200px] shrink-0">
						<div className="sticky top-[60px]">
							<BoardSidebar activeBoardSlug={activeBoardSlug} activeType={activeType} onSelectBoard={handleBoardSelect} onSelectType={handleBoardTypeSelect} />
						</div>
					</aside>
				)}

				{/* ── Center: Feed / Detail ── */}
				<main className="flex-1 min-w-0">
					{/* Tabs bar — standalone, aligns with left nav and right sidebar */}
					<div className="flex items-center gap-2 mb-2">
						<CommunityTabs active={activeTab} onChange={setActiveTab} />

						{/* Right side: active tag filter + sort dropdown */}
						<div className="ml-auto flex items-center gap-2">
							{activeTag && (
								<span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-caption bg-primary/10 text-primary font-medium">
									#{activeTag}
									<button className="ml-0.5 text-primary/60 hover:text-primary transition-colors" onClick={() => setActiveTag(null)} type="button">
										✕
									</button>
								</span>
							)}

							{/* Type dropdown — only when a topic board is selected */}
							{activeBoardSlug && (
								<DropdownMenu>
									<DropdownMenuTrigger asChild>
										<button
											aria-label="类型筛选"
											className={`flex items-center gap-1 h-7 px-2 rounded-md text-xs font-medium transition-colors shrink-0 ${
												activeType ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-foreground/5'
											}`}
											type="button"
										>
											<Layers className="h-3.5 w-3.5" />
											{activeType ? (TYPE_BOARDS.find((t) => t.abilityType === activeType)?.name ?? '全部') : '全部'}
										</button>
									</DropdownMenuTrigger>
									<DropdownMenuContent align="end" className="min-w-[140px] rounded-[17px] p-1.5" sideOffset={6}>
										<DropdownMenuItem className="gap-2 rounded-xl text-xs" onSelect={() => handleBoardTypeSelect(undefined)}>
											<span className="flex-1">全部</span>
											{!activeType && <span className="text-micro text-primary">✓</span>}
										</DropdownMenuItem>
										{TYPE_BOARDS.map((tb) => (
											<DropdownMenuItem className="gap-2 rounded-xl text-xs" key={tb.slug} onSelect={() => handleBoardTypeSelect(tb.abilityType)}>
												<span className="flex-1">{tb.name}</span>
												{activeType === tb.abilityType && <span className="text-micro text-primary">✓</span>}
											</DropdownMenuItem>
										))}
									</DropdownMenuContent>
								</DropdownMenu>
							)}

							{/* Sort dropdown */}
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<button
										aria-label="排序方式"
										className="flex items-center justify-center w-7 h-7 rounded-md text-muted-foreground hover:text-foreground hover:bg-foreground/5 transition-colors shrink-0"
										type="button"
									>
										<Filter className="h-3.5 w-3.5" />
									</button>
								</DropdownMenuTrigger>
								<DropdownMenuContent align="end" className="min-w-[140px] rounded-[17px] p-1.5" sideOffset={6}>
									{SORT_OPTIONS.map((opt) => {
										const Icon = opt.icon;
										const isActive = sort === opt.key;
										const isDesc = sortDirection === 'desc';
										const dirLabel = isDesc ? opt.descLabel : opt.ascLabel;
										const dirColor = isDesc ? 'text-destructive' : 'text-primary';
										const DirIcon = isDesc ? ArrowDown : ArrowUp;
										return (
											<DropdownMenuItem
												className="gap-2 rounded-xl text-xs"
												key={opt.key}
												onSelect={() => {
													if (sort === opt.key) {
														setSortDirection((d) => (d === 'desc' ? 'asc' : 'desc'));
													} else {
														setSort(opt.key);
														setSortDirection('desc');
													}
												}}
											>
												<Icon className="h-3.5 w-3.5 shrink-0" />
												<span className="flex-1">{opt.label}</span>
												{isActive && (
													<span className={`inline-flex items-center gap-0.5 ${dirColor}`}>
														<span className="text-micro">{dirLabel}</span>
														<DirIcon className="h-3 w-3 shrink-0" />
													</span>
												)}
											</DropdownMenuItem>
										);
									})}
								</DropdownMenuContent>
							</DropdownMenu>
						</div>
					</div>

					{/* Feed card — masonry post cards */}
					<PostFeed
						key={`${activeTab}-${activeBoardSlug ?? 'all'}`}
						columns={COMMUNITY_FEED_COLUMNS}
						hasMore={hasMore}
						isLoading={isLoading}
						onLoadMore={loadMore}
						posts={posts}
						renderCard={(post) => (
							<PostCard
								onClick={(p) => {
									window.open(`/community/post/${p.id}`, '_blank');
								}}
								onFavorite={handleFavorite}
								onLike={handleLike}
								onTagClick={handleTagClick}
								post={post}
							/>
						)}
					/>
				</main>

				{/* ── Right sidebar — sticky aside (hidden < 1280px in feed, always shown in detail if > 1024px) ── */}
				{!hideRightSidebar && (
					<aside className="w-[260px] shrink-0">
						<div className="sticky top-[60px]">
							<CommunityRightSidebar activeTag={activeTag} onTagClick={handleTagClick} />
						</div>
					</aside>
				)}
			</div>
		</div>
	);
}
